import { useState, useEffect } from 'react';
import styles from './VastraPurposeOther.module.scss';
import { Box, FormControl, Grid, InputAdornment, Typography } from '@mui/material';
import {
   CommonButton,
   CommonOutlinedButton,
   CustomInputLabel,
   CustomLeftIcon,
   CustomMenuItem,
   CustomRequiredInputLabel,
   CustomSelect,
   CustomTextFeild,
} from '../../common/StyledComponents/StyledFields';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import toast, { Toaster } from 'react-hot-toast';
import { object, string, ref, ValidationError } from 'yup';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import { isNil } from 'lodash';
import commonStyles from '../../../App.module.scss';
import SuccessPopup from '../../devotee/donation/successPopup';
import LOCALE from '../../../config/en-us.json';
import ErrorPopup from '../../common/ErrorPopup';
import constants from '../../../config/constants.json';
import apiPath from '../../../config/apiPaths.json';
import { ClipLoader } from 'react-spinners';

interface VastraPurpose {
   id: string;
   vastraName: string;
   description: string;
   amount: string;
}

interface VastraPuposeProps {
   handleBack?: () => void;
   selectData?: any;
   setShowTable?: any;
}
interface ErrorMessageTypes {
   vastraName: string;
   vastrapurposeId: string;
   description: string;
   vastraType: string;
   amount: string;
}

const VastraPurposeOther = (props: VastraPuposeProps): JSX.Element => {
   const { selectData } = props || {};
   const formFields = {
      vastraName: '',
      vastrapurposeId: '',
      description: '',
      amount: '',
      vastraType: '',
   };
   const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
      ...formFields,
   });
   const [formData, setFormData] = useState({ ...formFields });
   const [vastraTypeName, setVastraTypeName] = useState<VastraPurpose[]>([]);
   const [isOpen, setIsOpen] = useState<boolean>(false);
   const [submitLoading, setSubmitLoading] = useState(false);
   const handleFieldChange = (field: any, value: any): any => {
      setErrorMessage({ ...errorMessage, vastraType: '' });
      const selectedVastraType = vastraTypeName.find((item: any) => item.id === value);
      const selectedAmount = selectedVastraType ? selectedVastraType.amount : formData.amount;
      setFormData({
         ...formData,
         amount: selectedAmount, // Assign selectedAmount
         [field]: selectedVastraType ? selectedVastraType.id : value,
      });
   };

   useEffect(() => {
      getVastrasAmount();
   }, []);

   const validationSchema = object().shape({
      vastraName: string().required('Name is required'),
      description: string().required('Description is required'),
      vastrapurposeId: string().required('Vastra Purpose Id is required'),
      vastraType: string().required('Vastra Type is required'),
      amount: string().required('Amount is required'),
   });
   const handleSave = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };

      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         saveApiCall();
      } catch (error: any) {
         // Handle validation errors

         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               const key = e.path as keyof ErrorMessageTypes;
               if (key in errors) {
                  errors[key] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };
   const getVastrasAmount = () => {
      apiRequest({
         method: 'GET',
         endpoint: apiPath.vastraTypes,
         includeToken: true,
      })
         .then((res) => {
            setVastraTypeName(res.data.content);
         })
         .catch((err) => {
            console.log(err);
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         });
   };
   const saveApiCall = () => {
      const reqBody = {
         vastraPurposeId: formData.vastrapurposeId,
         description: formData.description,
         name: formData.vastraName,
         vastraTypeId: formData.vastraType,
         amount: parseInt(formData.amount),
         vastraExchange: null,
         dateOrAdornment: null,
      };
      apiRequest({
         method: 'POST',
         endpoint: apiPath.vastraPurpose,
         body: JSON.stringify(reqBody),
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.VASTRA_PURPOSE;
            toast.custom(
               <SuccessPopup
                  headerMessage="Vastra Purspose"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
            );
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         });
   };

   const handleUpdate = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };

      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         updateDailyActivityApiCall();
      } catch (error: any) {
         // Handle validation errors
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };
   useEffect(() => {
      updateData();
   }, [selectData]);

   const updateData = () => {
      setFormData({
         vastrapurposeId: selectData?.vastraPurposeId || '',
         description: selectData?.description || '',
         vastraName: selectData?.name || '',
         vastraType: selectData?.vastraTypeId || '',
         amount: selectData?.amount || '',
      });
   };
   const updateDailyActivityApiCall = () => {
      const reqBody = {
         vastraPurposeId: formData?.vastrapurposeId,
         description: formData?.description,
         name: formData?.vastraName,
         vastraTypeId: formData?.vastraType,
         amount: parseInt(formData?.amount),
         vastraExchange: null,
         dateOrAdornment: null,
         id: selectData?.id,
      };
      apiRequest({
         method: 'PUT',
         endpoint: `${apiPath.vastraPurpose}/${selectData?.id}`,
         body: JSON.stringify(reqBody),
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.VASTRA_PURPOSE_UPDATE;
            toast.custom(
               <SuccessPopup
                  headerMessage="Details Update"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         });
   };
   const handleCancel = () => {
      updateData();
   };
   const handleClear = () => {
      setFormData({
         ...formFields,
      });
   };

   const handleKeyPress = (e: any) => {
      if (e.key === 'Enter') {
         e.preventDefault(); // Prevent the default form submission behavior
         if (selectData) {
            handleUpdate();
         }
         handleSave();
      }
   };
   const handleButtons = () => {
      return (
         <div>
            {!isNil(selectData?.id) ? (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleCancel}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleUpdate}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Update</span>
                        </>
                     ) : (
                        'Update'
                     )}
                  </CommonButton>
               </>
            ) : (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleClear}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleSave}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Add</span>
                        </>
                     ) : (
                        'Add'
                     )}
                  </CommonButton>
               </>
            )}
         </div>
      );
   };
   const handleback = () => {
      props.handleBack?.();
   };
   const VastraDetailsPageOther = (): JSX.Element => {
      const VastraDropdown = (): React.ReactElement => {
         return (
            <div style={{ minWidth: '100%', borderRadius: '8px' }}>
               <FormControl sx={{ width: '100%' }}>
                  <CustomSelect
                     defaultValue=""
                     id="grouped-select"
                     onChange={(e) => handleFieldChange('vastraType', e.target.value)}
                     value={formData?.vastraType}
                     MenuProps={{
                        PaperProps: {
                           sx: {
                              border: '1px solid #F05802',
                              '& ul': {
                                 paddingTop: 0,
                                 paddingBottom: 0,
                                 overflowY: 'auto',
                                 minHeight: 'auto',
                                 maxHeight: '300px',
                                 '&::-webkit-scrollbar': {
                                    width: '8px',
                                 },
                                 '&::-webkit-scrollbar-thumb': {
                                    background: '#F3C539',
                                    borderRadius: '6px',
                                 },
                              },
                           },
                        },
                     }}
                     className={errorMessage.vastraType ? styles.errorTextField : styles.textField}
                  >
                     {vastraTypeName.map((item: any, index: number) => (
                        <CustomMenuItem key={index} value={item.id}>
                           {item.name}
                        </CustomMenuItem>
                     ))}
                  </CustomSelect>
                  {errorMessage.vastraType ? (
                     <Typography sx={{ color: 'red', fontSize: '12px' }}>
                        {errorMessage.vastraType}
                     </Typography>
                  ) : (
                     ''
                  )}
               </FormControl>
            </div>
         );
      };
      const validateVastraPurposeName = (value: string) => {
         const regex = /^[a-zA-Z0-9\s]*$/;
         return regex.test(value);
      };
      return (
         <>
            <Toaster />
            <Grid sx={{ display: "flex" }} onClick={handleback}>
               <span style={{
                  marginTop: "6px",
                  marginRight: "7px"
               }}>
                  <CustomLeftIcon/>
               </span>
            <Typography className={styles.SubHeading}>Vastra Purpose</Typography>
            </Grid>
            <Grid className={styles.formLayout}>
               <form onKeyDown={handleKeyPress}>
                  <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
                     <Grid xs={12} sm={4} item>
                        {CustomRequiredInputLabel('Vastra Purpose ID', true)}
                        <CustomTextFeild
                           fullWidth
                           required
                           value={formData.vastrapurposeId}
                           onChange={(e) => {
                              handleFieldChange('vastrapurposeId', e.target.value);
                              setErrorMessage({ ...errorMessage, vastrapurposeId: '' });
                           }}
                           placeholder="Enter Vastra Purpose ID"
                           className={
                              errorMessage.vastrapurposeId
                                 ? styles.errorTextField
                                 : styles.textField
                           }
                        />
                        {errorMessage.vastrapurposeId ? (
                           <Typography sx={{ color: 'red', fontSize: '12px' }}>
                              {errorMessage.vastrapurposeId}
                           </Typography>
                        ) : (
                           ''
                        )}
                     </Grid>
                     <Grid xs={12} sm={4} item>
                        {CustomRequiredInputLabel('Name', true)}
                        <CustomTextFeild
                           fullWidth
                           required
                           value={formData.vastraName}
                           onChange={(e) => {
                              const value = e.target.value;
                              if (validateVastraPurposeName(value)) {
                                 setErrorMessage({ ...errorMessage, vastraName: '' });
                                 handleFieldChange('vastraName', value);
                              }
                           }}
                           placeholder="Enter Name"
                           className={
                              errorMessage.vastraName ? styles.errorTextField : styles.textField
                           }
                        />
                        {errorMessage.vastraName ? (
                           <Typography sx={{ color: 'red', fontSize: '12px' }}>
                              {errorMessage.vastraName}
                           </Typography>
                        ) : (
                           ''
                        )}
                     </Grid>
                  </Grid>

                  <Grid container spacing={2} style={{ marginBottom: '15px' }}>
                     <Grid xs={12} sm={4} item>
                        {CustomRequiredInputLabel('Vastra Type', true)}
                        {VastraDropdown()}
                     </Grid>
                     <Grid xs={12} sm={4} item sx={{ maxWidth: '400px' }}>
                        {CustomRequiredInputLabel('Amount', true)}
                        <CustomTextFeild
                           fullWidth
                           required
                           value={formData.amount}
                           onChange={(e) => {
                              let inputValue = e.target.value;
                              // Disallow single '0'
                              if (inputValue === '0') {
                                 inputValue = '';
                              }
                              // Allow numbers and a decimal point
                              const sanitizedInput = inputValue
                                 .replace(/[^0-9.]/g, '')
                                 .slice(0, 10);
                              handleFieldChange('amount', sanitizedInput);
                              setErrorMessage({ ...errorMessage, amount: '' });
                           }}
                           InputProps={{
                              startAdornment: (
                                 <InputAdornment position="start">
                                    <AttachMoneyIcon
                                       fontSize="small"
                                       sx={{position: 'absolute' ,  backgroundColor:"#fff9e4", paddingRight: "3px",
                                       height: "48px",
                                       width: "22px",
                                       left: "0px",
                                       borderRadius: "20px"}}
                                    />
                                 </InputAdornment>
                              ),
                           }}
                           placeholder="Enter Amount"
                           className={
                              errorMessage.amount ? styles.errorTextField : styles.textField
                           }
                        />
                        {errorMessage.amount ? (
                           <Typography sx={{ color: 'red', fontSize: '12px' }}>
                              {errorMessage.amount}
                           </Typography>
                        ) : (
                           ''
                        )}
                     </Grid>
                  </Grid>
               </form>
               <Grid container spacing={2}>
                  <Grid xs={12} sm={4} item>
                     {CustomRequiredInputLabel('Description', true)}
                     <textarea
                        className={
                           errorMessage.description ? styles.description : styles.descriptionText
                        }
                        value={formData?.description}
                        onChange={(e) => {
                           handleFieldChange('description', e.target.value);
                           setErrorMessage({ ...errorMessage, description: '' });
                        }}
                     />
                     {errorMessage.description ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.description}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
               </Grid>
            </Grid>
            {handleButtons()}
         </>
      );
   };

   return <Box className={styles.totalLayout}>{VastraDetailsPageOther()}</Box>;
};

export default VastraPurposeOther;
